/**
 * ProblemPickerModal — 문항 담기(계약 §3). 트리 없이 개념·라벨·난이도로 찾아 고른다.
 *
 * problem-hub 의 검색 패널과 같은 엔드포인트지만 쓰임이 다르다: 저기는 "한 건을 열어 본다",
 * 여기는 "여러 건을 골라 담는다". 그래서 다중 선택이 축이고, **이미 담긴 문제는 다시 담기지
 * 않도록** 표시한다(같은 문제가 두 번 출제되는 사고가 가장 흔한 실수다).
 */
import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import MathName from '../SyllabusV2/MathName';
import { Search, ChevronLeft, ChevronRight, Check, Eye } from 'lucide-react';
import { Modal, Button } from '../common';
import { useAuth } from '../../context/AuthContext';
import { searchProblems, getOriginDisplay, normalizeConceptNames, difficultyOptionLabel, getNeedBadges, difficultyBadgeClass } from '../ProblemHubV2/problemApi';
// 문제 번호 — 두 메뉴가 함께 쓰는 정본 부품(문제 v2 목록 표가 기준 얼굴이다)
import ProblemIdLink from '../ProblemHubV2/ProblemIdLink';

// 검색이 주는 preview 는 평문 140자라 수식이 원문으로 보인다. 실제 지면을 보려면 본문을
// Lexical→HTML+KaTeX 로 그리는 이 창을 쓴다. lexical·katex 가 무거워 필요할 때만 내려받는다.
// v2 전용 미리보기 — 이 폴더의 다른 두 자리(PaperComposeModal·PaperItemsEditor)가 이미
// 쓰고 있던 것이다. 여기만 구형에 남아 한 화면 안에서 v1 과 v2 가 섞여 있었다.
const ProblemPreviewV2 = lazy(() => import('./ProblemPreviewV2'));

// 한 번에 펴 볼 수 있는 문항 수. 종전 근거는 「구형이 print/page 를 per_page=50 으로 한 번만
// 친다」였는데, 신형은 그 엔드포인트를 쓰지 않는다. 지금 근거는 **묶음 조회의 덩이 크기**다:
// 두 건 이상이면 problem-search 를 problem_ids 로 한 번 쳐서 받고(문제당 왕복이 아니다),
// 그 덩이가 100 이라 50 은 언제나 왕복 한 번에 들어온다(problemBodyV2 의 CHUNK).
const PREVIEW_MAX = 50;

const EMPTY = {
  conceptQ: '', labels: '', difficulty: '', problemIds: '',
  author: '', hasAnswer: '', hasSolution: '',
};

function ProblemPickerModal({ isOpen, existingIds = [], onClose, onAdd }) {
  const { getAccessToken } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [rows, setRows] = useState({ items: [], page: 1, totalPages: 1, totalItems: 0 });
  const [picked, setPicked] = useState([]); // 담기 버튼을 누르기 전까지의 선택(순서 유지)
  const [previewIds, setPreviewIds] = useState(null); // 미리보기로 띄운 문제들(null 이면 닫힘)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const seqRef = useRef(0);
  const seenRef = useRef(new Map()); // 넘긴 페이지의 선택도 미리보기를 들고 갈 수 있게

  const already = new Set(existingIds.map(String));

  const load = useCallback(async (f, page) => {
    const seq = (seqRef.current += 1);
    setLoading(true);
    setError(null);
    try {
      const filters = { page, perPage: 20 };
      if (f.conceptQ.trim()) filters.conceptQ = f.conceptQ.trim();
      if (f.labels.trim()) filters.labels = f.labels.split(',').map((s) => s.trim()).filter(Boolean).join(',');
      if (f.difficulty) filters.difficulty = f.difficulty;
      if (f.problemIds.trim()) filters.problemIds = f.problemIds.replace(/\s/g, '');
      if (f.author.trim()) filters.author = f.author.trim();
      if (f.hasAnswer !== '') filters.hasAnswer = f.hasAnswer === 'yes';
      if (f.hasSolution !== '') filters.hasSolution = f.hasSolution === 'yes';
      const d = await searchProblems(filters, getAccessToken);
      if (seq !== seqRef.current) return;
      d.items.forEach((it) => seenRef.current.set(String(it.problem_id), it));
      setRows(d);
    } catch (e) {
      if (seq !== seqRef.current) return;
      setError(e.message || '문제를 불러오지 못했습니다.');
      setRows({ items: [], page: 1, totalPages: 1, totalItems: 0 });
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [getAccessToken]);

  // 열릴 때 초기화 + 전체 목록 1페이지(필터를 안 주면 전체가 나온다 — 계약 §2)
  useEffect(() => {
    if (!isOpen) return undefined;
    setForm(EMPTY);
    setPicked([]);
    setPreviewIds(null);
    // 지난번 결과를 비운다 — 안 비우면 조건은 초기화됐는데 옛 목록이 그대로 보이고,
    // 스켈레톤도 (items 가 비어 있지 않아) 뜨지 않는다.
    setRows({ items: [], page: 1, totalPages: 1, totalItems: 0 });
    seenRef.current = new Map();
    load(EMPTY, 1);
    return () => { seqRef.current += 1; }; // 닫는 중에 도착할 응답 무시
  }, [isOpen, load]);

  // 미리보기(ProblemPreviewV2)는 공용 Modal 이라 ESC 를 스스로 받는다. 그런데도 이 리스너가
  // 필요하다 — 아래 Modal 의 ESC 를 함께 막지 않으면 미리보기를 닫으려던 키가 담기 창까지 닫아
  // 고른 것이 통째로 사라진다. 여기서 먼저 잡고 closeOnEsc 로 아래를 잠근다.
  // (PaperItemsEditor:407 이 같은 짝을 같은 이유로 두고 있다.)
  useEffect(() => {
    if (previewIds == null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPreviewIds(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [previewIds]);

  if (!isOpen) return null;

  const toggle = (id) => {
    if (already.has(String(id))) return;
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };

  const submit = () => {
    if (picked.length === 0) return;
    // id 만 넘기면 담긴 문항이 번호만 남는다 — 방금 보고 고른 미리보기를 함께 들려 보낸다
    onAdd(picked.map((id) => {
      const it = seenRef.current.get(String(id));
      return {
        problem_id: id,
        preview: it?.preview ?? '',
        difficulty: it?.difficulty ?? null,
        labels: it?.labels ?? '',
      };
    }));
    onClose();
  };

  const runSearch = () => load(form, 1);

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="문항 담기"
      size="xl"
      maxHeight="88vh"
      bodyClassName="flex min-h-0 flex-1 flex-col p-0"
      /* 미리보기는 공용 Modal 의 기본 z(9999980)로 뜬다 — 담기 창이 같은 값이면 나중에 마운트된
         쪽이 위에 서는 것에 기대게 되므로, 그동안만 이쪽을 명시적으로 아래로 내린다.
         (구형은 9999960 에 고정된 제 포털을 썼다 — 그때도 이 한 줄이 필요했다.) */
      zIndex={previewIds ? 9999950 : undefined}
      closeOnEsc={previewIds == null}
      headerActions={
        <span className="mr-2 text-xs text-gray-400">{loading ? '읽는 중…' : `${rows.totalItems}건`}</span>
      }
      footer={
        <div className="flex w-full items-center gap-2">
          {/* 페이지를 넘겨도 선택이 유지되므로 개수를 항상 보인다 — 안 보이면 넘긴 페이지의
              선택을 잊고 [담기]를 눌러 예상보다 많이 담게 된다 */}
          <p aria-live="polite" className="text-xs text-gray-500">
            {picked.length > 0 ? `${picked.length}개 선택됨 — 담은 순서대로 뒤에 붙습니다` : '문제를 골라 주세요'}
          </p>
          <div className="ml-auto flex gap-2">
            {picked.length > 0 && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPreviewIds(picked.slice(0, PREVIEW_MAX))}
                  title={picked.length > PREVIEW_MAX ? `한 번에 ${PREVIEW_MAX}개까지 보여 줍니다` : undefined}
                >
                  <Eye className="mr-0.5 inline size-3" />
                  고른 {Math.min(picked.length, PREVIEW_MAX)}개 보기
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setPicked([])}>선택 해제</Button>
              </>
            )}
            <Button size="sm" onClick={submit} disabled={picked.length === 0}>
              {picked.length > 0 ? `${picked.length}개 담기` : '담기'}
            </Button>
          </div>
        </div>
      }
    >
        <div className="shrink-0 space-y-2 border-b bg-gray-50 px-4 py-3">
          <div className="flex gap-1.5">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-gray-300" />
              <input
                type="text"
                value={form.conceptQ}
                onChange={(e) => setForm({ ...form, conceptQ: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) runSearch(); }}
                aria-label="개념명 검색"
                placeholder="개념명으로 검색(비우면 전체)"
                className="w-full rounded-lg border py-1.5 pl-8 pr-2 text-sm focus:border-primary focus:outline-none"
              />
            </div>
            <select
              value={form.difficulty}
              onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
              aria-label="난이도"
              className="rounded-lg border px-2 py-1.5 text-xs focus:border-primary focus:outline-none"
            >
              <option value="">난이도 전체</option>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>난이도 {difficultyOptionLabel(n)}</option>)}
            </select>
            <Button size="sm" onClick={runSearch} disabled={loading}>검색</Button>
          </div>
          <div className="flex gap-1.5">
            <input
              type="text"
              value={form.labels}
              onChange={(e) => setForm({ ...form, labels: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) runSearch(); }}
              aria-label="라벨"
              placeholder="라벨 — 콤마로 여러 개(모두 포함)"
              className="min-w-0 flex-1 rounded-lg border px-2 py-1.5 text-xs focus:border-primary focus:outline-none"
            />
            <input
              type="text"
              value={form.problemIds}
              onChange={(e) => setForm({ ...form, problemIds: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) runSearch(); }}
              aria-label="문제 ID"
              placeholder="문제 ID — 콤마로 여러 개"
              className="min-w-0 flex-1 rounded-lg border px-2 py-1.5 text-xs focus:border-primary focus:outline-none"
            />
          </div>
          {/* 시험지에 담을 문항이라 "정답·풀이가 있는가"가 실제로 중요하다(계약 §3의 필터) */}
          <div className="flex flex-wrap gap-1.5">
            <input
              type="text"
              value={form.author}
              onChange={(e) => setForm({ ...form, author: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) runSearch(); }}
              aria-label="작성자"
              placeholder="작성자"
              className="min-w-0 flex-1 rounded-lg border px-2 py-1.5 text-xs focus:border-primary focus:outline-none"
            />
            <label className="flex items-center gap-1 text-[11px] text-gray-500">
              정답
              <select
                value={form.hasAnswer}
                onChange={(e) => setForm({ ...form, hasAnswer: e.target.value })}
                className="rounded border px-1 py-0.5 text-xs focus:border-primary focus:outline-none"
              >
                <option value="">전체</option>
                <option value="yes">있음</option>
                <option value="no">없음</option>
              </select>
            </label>
            <label className="flex items-center gap-1 text-[11px] text-gray-500">
              풀이
              <select
                value={form.hasSolution}
                onChange={(e) => setForm({ ...form, hasSolution: e.target.value })}
                className="rounded border px-1 py-0.5 text-xs focus:border-primary focus:outline-none"
              >
                <option value="">전체</option>
                <option value="yes">있음</option>
                <option value="no">없음</option>
              </select>
            </label>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
          {error && (
            <div className="space-y-2 p-3">
              <p role="alert" className="text-sm text-danger">{error}</p>
              <Button size="xs" variant="secondary" onClick={() => load(form, rows.page)}>다시 시도</Button>
            </div>
          )}
          {!error && loading && rows.items.length === 0 && (
            <div className="space-y-2 py-2">
              {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-12 rounded-xl bg-gray-100 motion-safe:animate-pulse" />)}
            </div>
          )}
          {!error && !loading && rows.items.length === 0 && (
            <p className="p-6 text-center text-sm text-gray-400">조건에 맞는 문제가 없습니다 — 조건을 줄여 보세요.</p>
          )}

          {!error && rows.items.length > 0 && (
            <p className="px-1 pb-1 text-[11px] text-gray-400">
              아래 본문은 평문 요약이라 수식이 원문 그대로 보입니다 — 눈 아이콘으로 실제 문제를 확인하세요.
            </p>
          )}

          <ul className="space-y-1">
            {rows.items.map((it) => {
              const id = it.problem_id;
              const isIn = already.has(String(id));
              const sel = picked.includes(id);
              const origin = getOriginDisplay(it);
              const names = normalizeConceptNames(it.concept_names);
              // 계약 §1-3 — 난이도가 없는 문항이 섞이면 배포가 409(missing_difficulty)로 막힌다.
              // 그 사실을 **담기 전에** 말해 주는 자리가 검색 응답의 need_* 배지다(examPaperApi
              // assignPaper 주석과 같은 절). 목록·순서·문구는 problemApi 의 정본을 그대로 쓴다 —
              // 앞의 둘(개념·난이도)만 needs_analysis 필터에 걸려 순서에 뜻이 있다.
              const needs = getNeedBadges(it);
              return (
                /* 담기 토글과 미리보기는 서로 다른 조작이라 버튼도 둘이어야 한다
                   (한 버튼 안에 버튼을 넣을 수 없으므로 테두리·배경은 li 가 진다) */
                <li
                  key={id}
                  className={`flex items-center gap-1 rounded-xl border transition-colors ${
                    isIn ? 'border-gray-100 bg-gray-50 opacity-60'
                      : sel ? 'border-primary/40 bg-primary/5' : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  {/* 번호는 **담기 버튼 밖**에 선다(2026-09-01): 누르면 그 문제의 상세로 가는
                      링크인데, 버튼 안에 anchor 를 넣으면 버튼 안의 버튼이 된다.
                      자리는 맨 왼쪽 — 기준으로 삼은 문제 v2 의 표에서도 PID 가 첫 칸이다.
                      이미 담긴 행은 담기 버튼이 disabled 지만 번호는 살아 있다 — 「담을 수 없다」와
                      「볼 수 없다」는 다른 말이다. */}
                  <ProblemIdLink id={id} item={it} className="shrink-0 pl-2" />
                  <button
                    type="button"
                    onClick={() => toggle(id)}
                    disabled={isIn}
                    aria-pressed={sel}
                    /* 번호가 버튼 밖으로 나가면서 읽어 주는 이름에서 빠졌다 — 이름을 못박는다.
                       같은 모양의 버튼이 스무 개 세로로 서는 목록이라 이름이 곧 구별이다. */
                    aria-label={`${id}번 문제 고르기`}
                    className="min-w-0 flex-1 rounded-xl py-2 pl-1 pr-2 text-left disabled:cursor-not-allowed"
                  >
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span
                        aria-hidden="true"
                        className={`flex size-4 shrink-0 items-center justify-center rounded border ${
                          sel ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {sel && <Check className="size-3" />}
                      </span>
                      {isIn && <span className="shrink-0 rounded-full bg-gray-200 px-1.5 py-0.5 text-[9px] text-gray-600">이미 담김</span>}
                      {origin && <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium ${origin.cls}`}>{origin.text}</span>}
                      {it.difficulty != null && (
                        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] ${difficultyBadgeClass(it.difficulty)}`}>난이도 {it.difficulty}</span>
                      )}
                      {needs.map((n) => (n.key === 'need_difficulty' ? (
                        /* 넷 중 이것만 배포를 막는다 — 같은 amber 계열 안에서 테두리·굵기·한 단
                           진한 글자로 올린다. PaperItemsEditor 의 '삭제된 문항' 배지와 같은 값이다
                           (그쪽도 §1-3 의 409 다 — 배포를 막는 두 조건이 같은 얼굴을 갖는다). */
                        <span
                          key={n.key}
                          className="shrink-0 rounded-full border border-amber-200 bg-white px-1.5 py-0.5 text-[9px] font-medium text-amber-800"
                          title="난이도가 없습니다 — 이 문항이 담기면 배포가 막힙니다(분석이 필요합니다)"
                        >
                          {n.label} 없음
                        </span>
                      ) : (
                        <span key={n.key} className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] ${n.cls}`}>{n.label} 없음</span>
                      )))}
                      {it.author && <span className="ml-auto shrink-0 text-[10px] text-gray-400">{it.author}</span>}
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-gray-700">
                      {it.preview || <span className="text-gray-300">본문 미리보기 없음</span>}
                    </p>
                    {(it.labels || names.length > 0) && (
                      <p className="mt-1 truncate text-[10px] text-gray-400">
                        {it.labels}
                        {it.labels && names.length > 0 ? ' · ' : ''}
                        <MathName text={names.join(', ')} compact />
                      </p>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewIds([id])}
                    aria-label={`${id}번 문제 미리보기`}
                    title="수식까지 그린 실제 문제 보기"
                    className="mr-1 shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-white hover:text-primary focus:text-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <Eye className="size-4" />
                  </button>
                </li>
              );
            })}
          </ul>

          {rows.totalPages > 1 && (
            <div className="mt-2 flex items-center justify-center gap-2 text-xs text-gray-500">
              <button
                type="button"
                disabled={loading || rows.page <= 1}
                onClick={() => load(form, rows.page - 1)}
                aria-label="이전 페이지"
                className="rounded-full p-1 hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronLeft className="size-4" />
              </button>
              {rows.page} / {rows.totalPages}
              <button
                type="button"
                disabled={loading || rows.page >= rows.totalPages}
                onClick={() => load(form, rows.page + 1)}
                aria-label="다음 페이지"
                className="rounded-full p-1 hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
          )}
        </div>

    </Modal>

    {/* Modal 밖에 둔다 — 안에 두면 Modal 의 Tab 가두기가 미리보기 안의 초점을 도로 끌어간다 */}
    {previewIds && (
      <Suspense fallback={null}>
        {/* correctList·problemNumbers 를 넘기지 않는다 — 구형은 빈 배열을 받아야 했지만
            신형은 기본값이 빈 배열이다. 담기 **전**이라 번호도 정오답도 아직 없는 자리다. */}
        <ProblemPreviewV2
          isOpen
          onClose={() => setPreviewIds(null)}
          problemIds={previewIds}
          showNumbers={false}
        />
      </Suspense>
    )}
    </>
  );
}

export default ProblemPickerModal;
